/**
 * Full Claim Entry Tab (Tab 3)
 * Doula scanned document -> OCR review -> Claims creation
 * Side-by-side document viewer + extracted fields with confidence scoring
 * Extracted from original DocumentProcessingPage - behavior unchanged
 */

import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { getWeaveinApiUrl } from '@/src/config';
import {
  FileText,
  Upload,
  Search,
  RefreshCw,
  CheckCircle,
  XCircle,
  Eye,
  ArrowLeft,
  AlertTriangle,
  Clock,
  FileCheck,
  Loader2,
  ScanLine,
  BarChart3,
  User,
  Zap,
  Trash2,
} from 'lucide-react';
import documentProcessingApi, {
  type QueueItem,
  type QueueItemStatus,
  type ProcessingStats,
  type ExtractedField,
  type ReviewAction,
} from '@/services/api/documentProcessing';

interface FullClaimEntryTabProps {
  onNavigate?: (tab: string) => void;
}

const STATUS_CONFIG: Record<QueueItemStatus, { label: string; color: string; icon: React.ReactNode }> = {
  pending: { label: 'Pending', color: 'bg-gray-100 text-gray-700', icon: <Clock className="w-3 h-3" /> },
  processing: { label: 'Processing', color: 'bg-blue-100 text-blue-700', icon: <Loader2 className="w-3 h-3 animate-spin" /> },
  extracted: { label: 'Extracted', color: 'bg-yellow-100 text-yellow-700', icon: <ScanLine className="w-3 h-3" /> },
  review: { label: 'In Review', color: 'bg-orange-100 text-orange-700', icon: <Eye className="w-3 h-3" /> },
  approved: { label: 'Approved', color: 'bg-green-100 text-green-700', icon: <CheckCircle className="w-3 h-3" /> },
  claim_created: { label: 'Claim Created', color: 'bg-emerald-100 text-emerald-700', icon: <FileCheck className="w-3 h-3" /> },
  failed: { label: 'Failed', color: 'bg-red-100 text-red-700', icon: <XCircle className="w-3 h-3" /> },
  rejected: { label: 'Rejected', color: 'bg-red-100 text-red-700', icon: <XCircle className="w-3 h-3" /> },
};

function getConfidenceColor(confidence: number): string {
  if (confidence >= 0.9) return 'bg-green-50 border-green-200';
  if (confidence >= 0.7) return 'bg-yellow-50 border-yellow-200';
  return 'bg-red-50 border-red-200';
}

function getConfidenceBadge(confidence: number): { label: string; color: string } {
  if (confidence >= 0.9) return { label: `${Math.round(confidence * 100)}%`, color: 'bg-green-100 text-green-700' };
  if (confidence >= 0.7) return { label: `${Math.round(confidence * 100)}%`, color: 'bg-yellow-100 text-yellow-700' };
  if (confidence > 0) return { label: `${Math.round(confidence * 100)}%`, color: 'bg-red-100 text-red-700' };
  return { label: 'N/A', color: 'bg-gray-100 text-gray-500' };
}

function formatFieldLabel(claimField: string): string {
  return claimField
    .replace(/_/g, ' ')
    .replace(/\b\w/g, (c) => c.toUpperCase());
}

export default function FullClaimEntryTab({ onNavigate }: FullClaimEntryTabProps) {
  // View state
  const [view, setView] = useState<'list' | 'detail'>('list');
  const [selectedItemId, setSelectedItemId] = useState<number | null>(null);

  // List state
  const [items, setItems] = useState<QueueItem[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [statusFilter, setStatusFilter] = useState<string>('');
  const [searchQuery, setSearchQuery] = useState('');
  const [stats, setStats] = useState<ProcessingStats | null>(null);

  // Detail state
  const [selectedItem, setSelectedItem] = useState<QueueItem | null>(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [corrections, setCorrections] = useState<Record<string, string>>({});
  const [actionLoading, setActionLoading] = useState(false);
  const [rejectionReason, setRejectionReason] = useState('');

  // Upload state
  const [uploading, setUploading] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // ---- Data Loading ----

  const loadQueue = useCallback(async () => {
    setLoading(true);
    try {
      const response = await documentProcessingApi.listQueue({
        status: statusFilter || undefined,
        search: searchQuery || undefined,
        document_type: 'doula_visit_form',
        limit: 50,
      });
      setItems(response.items);
      setTotal(response.total);
    } catch (err) {
      console.error('Failed to load queue:', err);
    } finally {
      setLoading(false);
    }
  }, [statusFilter, searchQuery]);

  const loadStats = useCallback(async () => {
    try {
      const data = await documentProcessingApi.getStats('doula_visit_form');
      setStats(data);
    } catch (err) {
      console.error('Failed to load stats:', err);
    }
  }, []);

  const loadDetail = useCallback(async (id: number) => {
    setDetailLoading(true);
    try {
      const data = await documentProcessingApi.getQueueItem(id);
      setSelectedItem(data);
      // Pre-populate corrections from stored data so they persist across refresh
      const stored = data.extraction_result?.corrections;
      if (stored && typeof stored === 'object' && Object.keys(stored).length > 0) {
        const restored: Record<string, string> = {};
        for (const [key, val] of Object.entries(stored)) {
          if (val !== null && val !== undefined) {
            restored[key] = String(val);
          }
        }
        setCorrections(restored);
      } else {
        setCorrections({});
      }
    } catch (err) {
      console.error('Failed to load detail:', err);
    } finally {
      setDetailLoading(false);
    }
  }, []);

  useEffect(() => {
    loadQueue();
    loadStats();
  }, [loadQueue, loadStats]);

  useEffect(() => {
    if (selectedItemId) {
      loadDetail(selectedItemId);
    }
  }, [selectedItemId, loadDetail]);

  // ---- Actions ----

  const handleUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    setUploading(true);
    try {
      const result = await documentProcessingApi.uploadDocument(file);
      // Auto-process the uploaded document
      try {
        await documentProcessingApi.processDocument(result.id);
      } catch {
        // Processing may fail if Azure DI is not configured - that's OK
      }
      loadQueue();
      loadStats();
    } catch (err: any) {
      alert(err.response?.data?.detail || 'Upload failed');
    } finally {
      setUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  const handleProcess = async (id: number) => {
    setActionLoading(true);
    try {
      await documentProcessingApi.processDocument(id);
      loadDetail(id);
      loadQueue();
      loadStats();
    } catch (err: any) {
      alert(err.response?.data?.detail || 'Processing failed');
    } finally {
      setActionLoading(false);
    }
  };

  const handleReprocess = async (id: number) => {
    setActionLoading(true);
    try {
      await documentProcessingApi.reprocessDocument(id);
      loadDetail(id);
      loadQueue();
    } catch (err: any) {
      alert(err.response?.data?.detail || 'Reprocessing failed');
    } finally {
      setActionLoading(false);
    }
  };

  const handleApprove = async (id: number) => {
    setActionLoading(true);
    try {
      const hasCorrections = Object.keys(corrections).length > 0;
      await documentProcessingApi.submitReview(id, {
        action: 'approve',
        corrections: hasCorrections ? corrections : undefined,
      });
      loadDetail(id);
      loadQueue();
      loadStats();
    } catch (err: any) {
      alert(err.response?.data?.detail || 'Approval failed');
    } finally {
      setActionLoading(false);
    }
  };

  const handleReject = async (id: number) => {
    if (!rejectionReason.trim()) {
      alert('Please provide a rejection reason');
      return;
    }
    setActionLoading(true);
    try {
      await documentProcessingApi.submitReview(id, {
        action: 'reject',
        rejection_reason: rejectionReason,
      });
      setRejectionReason('');
      loadDetail(id);
      loadQueue();
      loadStats();
    } catch (err: any) {
      alert(err.response?.data?.detail || 'Rejection failed');
    } finally {
      setActionLoading(false);
    }
  };

  const handleCreateClaim = async (id: number) => {
    setActionLoading(true);
    try {
      const result = await documentProcessingApi.createClaimFromExtraction(
        id,
        Object.keys(corrections).length > 0 ? corrections : undefined
      );
      if (result.success) {
        alert(`Claim ${result.claim_number} created successfully!`);
        loadDetail(id);
        loadQueue();
        loadStats();
      }
    } catch (err: any) {
      alert(err.response?.data?.detail || 'Claim creation failed');
    } finally {
      setActionLoading(false);
    }
  };

  const handleDelete = async (id: number, fileName?: string) => {
    const displayName = fileName || `Document #${id}`;
    if (!confirm(`Are you sure you want to delete "${displayName}"? This action cannot be undone.`)) {
      return;
    }
    setActionLoading(true);
    try {
      await documentProcessingApi.deleteQueueItem(id);
      if (view === 'detail') {
        backToList();
      } else {
        loadQueue();
      }
      loadStats();
    } catch (err: any) {
      alert(err.response?.data?.detail || 'Delete failed');
    } finally {
      setActionLoading(false);
    }
  };

  const openDetail = (id: number) => {
    setSelectedItemId(id);
    setView('detail');
  };

  const backToList = () => {
    setView('list');
    setSelectedItemId(null);
    setSelectedItem(null);
    setCorrections({});
    loadQueue();
  };

  // ---- Stats Cards ----

  const renderStats = () => {
    if (!stats) return null;

    const cards = [
      { label: 'Total', value: stats.total_documents, icon: <FileText className="w-4 h-4 text-gray-500" /> },
      { label: 'Pending', value: stats.pending, icon: <Clock className="w-4 h-4 text-gray-500" /> },
      { label: 'Extracted', value: stats.extracted + stats.in_review, icon: <ScanLine className="w-4 h-4 text-yellow-500" /> },
      { label: 'Approved', value: stats.approved, icon: <CheckCircle className="w-4 h-4 text-green-500" /> },
      { label: 'Claims Created', value: stats.claims_created, icon: <FileCheck className="w-4 h-4 text-emerald-500" /> },
      { label: 'Avg Confidence', value: `${Math.round(stats.avg_confidence * 100)}%`, icon: <BarChart3 className="w-4 h-4 text-blue-500" /> },
    ];

    return (
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3 mb-6">
        {cards.map((card) => (
          <Card key={card.label} className="p-3">
            <div className="flex items-center gap-2 mb-1">
              {card.icon}
              <span className="text-xs text-gray-500">{card.label}</span>
            </div>
            <div className="text-xl font-semibold">{card.value}</div>
          </Card>
        ))}
      </div>
    );
  };

  // ---- List View ----

  const renderList = () => {
    const statusTabs = [
      { value: '', label: 'All' },
      { value: 'pending', label: 'Pending' },
      { value: 'extracted', label: 'Extracted' },
      { value: 'review', label: 'In Review' },
      { value: 'approved', label: 'Approved' },
      { value: 'claim_created', label: 'Claims Created' },
      { value: 'failed', label: 'Failed' },
    ];

    return (
      <>
        {renderStats()}

        {/* Toolbar */}
        <div className="flex flex-col md:flex-row items-start md:items-center justify-between gap-3 mb-4">
          <div className="flex items-center gap-2 flex-wrap">
            {statusTabs.map((tab) => (
              <button
                key={tab.value}
                onClick={() => setStatusFilter(tab.value)}
                className={`px-3 py-1.5 text-sm rounded-md transition-colors ${
                  statusFilter === tab.value
                    ? 'bg-blue-100 text-blue-700 font-medium'
                    : 'text-gray-600 hover:bg-gray-100'
                }`}
              >
                {tab.label}
              </button>
            ))}
          </div>

          <div className="flex items-center gap-2">
            <div className="relative">
              <Search className="absolute left-2.5 top-2.5 w-4 h-4 text-gray-400" />
              <Input
                placeholder="Search documents..."
                value={searchQuery}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) => setSearchQuery(e.target.value)}
                className="pl-9 w-64"
                onKeyDown={(e: React.KeyboardEvent) => e.key === 'Enter' && loadQueue()}
              />
            </div>

            <Button variant="outline" size="sm" onClick={() => loadQueue()}>
              <RefreshCw className="w-4 h-4" />
            </Button>

            <div className="relative">
              <Button
                size="sm"
                onClick={() => fileInputRef.current?.click()}
                disabled={uploading}
              >
                {uploading ? (
                  <Loader2 className="w-4 h-4 mr-1 animate-spin" />
                ) : (
                  <Upload className="w-4 h-4 mr-1" />
                )}
                Upload Document
              </Button>
              <input
                ref={fileInputRef}
                type="file"
                accept=".pdf,.jpg,.jpeg,.png,.tiff,.bmp"
                onChange={handleUpload}
                className="hidden"
              />
            </div>
          </div>
        </div>

        {/* Table */}
        <Card>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b bg-gray-50">
                  <th className="text-left p-3 font-medium text-gray-600">Document</th>
                  <th className="text-left p-3 font-medium text-gray-600">Status</th>
                  <th className="text-left p-3 font-medium text-gray-600">Confidence</th>
                  <th className="text-left p-3 font-medium text-gray-600">Member Match</th>
                  <th className="text-left p-3 font-medium text-gray-600">Source</th>
                  <th className="text-left p-3 font-medium text-gray-600">Date</th>
                  <th className="text-left p-3 font-medium text-gray-600">Actions</th>
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr>
                    <td colSpan={7} className="text-center p-8 text-gray-500">
                      <Loader2 className="w-5 h-5 animate-spin mx-auto mb-2" />
                      Loading documents...
                    </td>
                  </tr>
                ) : items.length === 0 ? (
                  <tr>
                    <td colSpan={7} className="text-center p-8 text-gray-500">
                      <FileText className="w-8 h-8 mx-auto mb-2 text-gray-300" />
                      No documents found. Upload a scanned doula visit form to get started.
                    </td>
                  </tr>
                ) : (
                  items.map((item) => {
                    const statusCfg = STATUS_CONFIG[item.status] || STATUS_CONFIG.pending;
                    const confBadge = getConfidenceBadge(item.overall_confidence);

                    return (
                      <tr
                        key={item.id}
                        className="border-b hover:bg-gray-50 cursor-pointer"
                        onClick={() => openDetail(item.id)}
                      >
                        <td className="p-3">
                          <div className="flex items-center gap-2">
                            <FileText className="w-4 h-4 text-gray-400 flex-shrink-0" />
                            <div>
                              <div className="font-medium text-gray-900 truncate max-w-[200px]">
                                {item.source_file_name || `Document #${item.id}`}
                              </div>
                              {item.page_count && (
                                <div className="text-xs text-gray-400">{item.page_count} page(s)</div>
                              )}
                            </div>
                          </div>
                        </td>
                        <td className="p-3">
                          <span className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium ${statusCfg.color}`}>
                            {statusCfg.icon}
                            {statusCfg.label}
                          </span>
                        </td>
                        <td className="p-3">
                          {item.overall_confidence > 0 ? (
                            <span className={`inline-flex px-2 py-0.5 rounded text-xs font-medium ${confBadge.color}`}>
                              {confBadge.label}
                            </span>
                          ) : (
                            <span className="text-xs text-gray-400">--</span>
                          )}
                        </td>
                        <td className="p-3">
                          {item.member_name ? (
                            <div className="flex items-center gap-1">
                              <User className="w-3 h-3 text-gray-400" />
                              <span className="text-sm">{item.member_name}</span>
                            </div>
                          ) : (
                            <span className="text-xs text-gray-400">Not matched</span>
                          )}
                        </td>
                        <td className="p-3">
                          <span className="text-xs text-gray-500">
                            {item.source_type === 'sharepoint_sync' ? 'SharePoint' : 'Manual'}
                          </span>
                        </td>
                        <td className="p-3">
                          <span className="text-xs text-gray-500">
                            {new Date(item.created_at).toLocaleDateString()}
                          </span>
                        </td>
                        <td className="p-3">
                          <div className="flex items-center gap-1">
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={(e: React.MouseEvent) => {
                                e.stopPropagation();
                                openDetail(item.id);
                              }}
                            >
                              <Eye className="w-4 h-4" />
                            </Button>
                            {!item.claim_id && (
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={(e: React.MouseEvent) => {
                                  e.stopPropagation();
                                  handleDelete(item.id, item.source_file_name);
                                }}
                                className="text-red-500 hover:text-red-700 hover:bg-red-50"
                              >
                                <Trash2 className="w-4 h-4" />
                              </Button>
                            )}
                          </div>
                        </td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>
          {total > items.length && (
            <div className="p-3 border-t text-center text-sm text-gray-500">
              Showing {items.length} of {total} documents
            </div>
          )}
        </Card>
      </>
    );
  };

  // ---- Detail View ----

  const renderDetail = () => {
    if (detailLoading || !selectedItem) {
      return (
        <div className="flex items-center justify-center p-12">
          <Loader2 className="w-6 h-6 animate-spin text-blue-500" />
        </div>
      );
    }

    const extraction = selectedItem.extraction_result;
    const statusCfg = STATUS_CONFIG[selectedItem.status] || STATUS_CONFIG.pending;
    const canProcess = selectedItem.status === 'pending' || selectedItem.status === 'failed';
    const canReview = selectedItem.status === 'extracted' || selectedItem.status === 'review';
    const canCreateClaim = selectedItem.status === 'approved' || selectedItem.status === 'extracted' || selectedItem.status === 'review';
    const isTerminal = selectedItem.status === 'claim_created' || selectedItem.status === 'rejected';

    return (
      <>
        {/* Header */}
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-3">
            <Button variant="ghost" size="sm" onClick={backToList}>
              <ArrowLeft className="w-4 h-4 mr-1" />
              Back
            </Button>
            <h2 className="text-lg font-semibold">
              {selectedItem.source_file_name || `Document #${selectedItem.id}`}
            </h2>
            <span className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium ${statusCfg.color}`}>
              {statusCfg.icon}
              {statusCfg.label}
            </span>
          </div>

          <div className="flex items-center gap-2">
            {canProcess && (
              <Button size="sm" onClick={() => handleProcess(selectedItem.id)} disabled={actionLoading}>
                {actionLoading ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Zap className="w-4 h-4 mr-1" />}
                Process OCR
              </Button>
            )}
            {extraction && !isTerminal && (
              <Button variant="outline" size="sm" onClick={() => handleReprocess(selectedItem.id)} disabled={actionLoading}>
                <RefreshCw className="w-4 h-4 mr-1" />
                Reprocess
              </Button>
            )}
            {!selectedItem.claim_id && (
              <Button
                variant="outline"
                size="sm"
                onClick={() => handleDelete(selectedItem.id, selectedItem.source_file_name)}
                disabled={actionLoading}
                className="text-red-600 border-red-200 hover:bg-red-50"
              >
                <Trash2 className="w-4 h-4 mr-1" />
                Delete
              </Button>
            )}
          </div>
        </div>

        {/* Two-panel layout */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4" style={{ minHeight: '600px' }}>
          {/* Left: Document Viewer */}
          <Card className="p-0 overflow-hidden">
            <div className="bg-gray-50 px-4 py-2 border-b flex items-center gap-2">
              <FileText className="w-4 h-4 text-gray-500" />
              <span className="text-sm font-medium text-gray-700">Scanned Document</span>
            </div>
            <div className="h-[600px]">
              {selectedItem.storage_url ? (
                selectedItem.mime_type === 'application/pdf' ? (
                  <iframe
                    src={`${getWeaveinApiUrl()}${selectedItem.storage_url}`}
                    className="w-full h-full border-0"
                    title="Scanned Document"
                  />
                ) : (
                  <div className="flex items-center justify-center h-full bg-gray-100 p-4">
                    <img
                      src={`${getWeaveinApiUrl()}${selectedItem.storage_url}`}
                      alt="Scanned Document"
                      className="max-w-full max-h-full object-contain"
                    />
                  </div>
                )
              ) : (
                <div className="flex items-center justify-center h-full text-gray-400">
                  <div className="text-center">
                    <FileText className="w-12 h-12 mx-auto mb-2" />
                    <p>No document preview available</p>
                  </div>
                </div>
              )}
            </div>
          </Card>

          {/* Right: Extracted Fields */}
          <div className="space-y-4">
            {/* Extraction Summary */}
            {extraction && (
              <Card className="p-4">
                <h3 className="text-sm font-semibold text-gray-700 mb-3">Extraction Summary</h3>
                <div className="grid grid-cols-4 gap-3">
                  <div className="text-center">
                    <div className="text-lg font-bold text-gray-900">
                      {Math.round(extraction.overall_confidence * 100)}%
                    </div>
                    <div className="text-xs text-gray-500">Overall</div>
                  </div>
                  <div className="text-center">
                    <div className="text-lg font-bold text-green-600">{extraction.high_confidence_fields}</div>
                    <div className="text-xs text-gray-500">High</div>
                  </div>
                  <div className="text-center">
                    <div className="text-lg font-bold text-yellow-600">{extraction.medium_confidence_fields}</div>
                    <div className="text-xs text-gray-500">Medium</div>
                  </div>
                  <div className="text-center">
                    <div className="text-lg font-bold text-red-600">{extraction.low_confidence_fields}</div>
                    <div className="text-xs text-gray-500">Low</div>
                  </div>
                </div>
              </Card>
            )}

            {/* Extracted Fields */}
            {extraction && (
              <Card className="p-4">
                <h3 className="text-sm font-semibold text-gray-700 mb-3">Extracted Fields</h3>
                <div className="space-y-3">
                  {Object.entries(extraction.extracted_fields).map(([fieldName, fieldData]) => {
                    const field = fieldData as ExtractedField;
                    const confColor = getConfidenceColor(field.confidence);
                    const confBadge = getConfidenceBadge(field.confidence);
                    const correctedValue = corrections[fieldName];
                    const displayValue = correctedValue !== undefined ? correctedValue : (field.value || '');

                    return (
                      <div key={fieldName} className={`p-3 rounded-md border ${confColor}`}>
                        <div className="flex items-center justify-between mb-1">
                          <label className="text-xs font-medium text-gray-600">
                            {formatFieldLabel(fieldName)}
                          </label>
                          <span className={`inline-flex px-1.5 py-0.5 rounded text-[10px] font-medium ${confBadge.color}`}>
                            {confBadge.label}
                          </span>
                        </div>
                        <Input
                          value={displayValue}
                          onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                            setCorrections((prev) => ({
                              ...prev,
                              [fieldName]: e.target.value,
                            }));
                          }}
                          disabled={isTerminal}
                          className="text-sm h-8 bg-white"
                          placeholder={`Enter ${formatFieldLabel(fieldName).toLowerCase()}`}
                        />
                        {correctedValue !== undefined && correctedValue !== (field.value || '') && (
                          <div className="text-[10px] text-blue-600 mt-1">
                            Corrected (original: {field.value || 'empty'})
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              </Card>
            )}

            {/* Member Match */}
            {selectedItem.matched_member_id && (
              <Card className="p-4">
                <h3 className="text-sm font-semibold text-gray-700 mb-2">Member Match</h3>
                <div className="flex items-center gap-2">
                  <User className="w-4 h-4 text-blue-500" />
                  <span className="font-medium">{selectedItem.member_name}</span>
                  <span className={`text-xs px-1.5 py-0.5 rounded ${getConfidenceBadge(selectedItem.member_match_confidence || 0).color}`}>
                    {selectedItem.member_match_method} ({Math.round((selectedItem.member_match_confidence || 0) * 100)}%)
                  </span>
                </div>
              </Card>
            )}

            {/* Suggested Claim Lines */}
            {extraction?.suggested_claim_data?.line_items && extraction.suggested_claim_data.line_items.length > 0 && (
              <Card className="p-4">
                <h3 className="text-sm font-semibold text-gray-700 mb-2">Suggested Claim Lines</h3>
                <div className="space-y-2">
                  {extraction.suggested_claim_data.line_items.map((li: any, idx: number) => (
                    <div key={idx} className="flex items-center justify-between p-2 bg-gray-50 rounded text-sm">
                      <div>
                        <span className="font-mono font-medium text-blue-700">{li.cpt_code}</span>
                        {li.modifier_1 && <span className="text-xs text-gray-500 ml-1">({li.modifier_1})</span>}
                        {li.service_description && (
                          <span className="text-xs text-gray-500 ml-2">{li.service_description}</span>
                        )}
                      </div>
                      <div className="text-right">
                        <span className="font-medium">${(li.charge_amount || 0).toFixed(2)}</span>
                        <span className="text-xs text-gray-500 ml-1">x{li.units}</span>
                      </div>
                    </div>
                  ))}
                </div>
              </Card>
            )}

            {/* Action Buttons */}
            {!isTerminal && extraction && (
              <Card className="p-4">
                <div className="space-y-3">
                  {canCreateClaim && (
                    <Button
                      className="w-full"
                      onClick={() => handleCreateClaim(selectedItem.id)}
                      disabled={actionLoading}
                    >
                      {actionLoading ? (
                        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      ) : (
                        <CheckCircle className="w-4 h-4 mr-2" />
                      )}
                      Approve & Create Claim
                    </Button>
                  )}

                  {canReview && (
                    <Button
                      variant="outline"
                      className="w-full"
                      onClick={() => handleApprove(selectedItem.id)}
                      disabled={actionLoading}
                    >
                      <CheckCircle className="w-4 h-4 mr-2" />
                      Approve (Review Later)
                    </Button>
                  )}

                  <div className="flex gap-2">
                    <Input
                      value={rejectionReason}
                      onChange={(e: React.ChangeEvent<HTMLInputElement>) => setRejectionReason(e.target.value)}
                      placeholder="Rejection reason..."
                      className="flex-1 text-sm"
                    />
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => handleReject(selectedItem.id)}
                      disabled={actionLoading || !rejectionReason.trim()}
                      className="text-red-600 border-red-200 hover:bg-red-50"
                    >
                      <XCircle className="w-4 h-4 mr-1" />
                      Reject
                    </Button>
                  </div>
                </div>
              </Card>
            )}

            {/* Terminal status info */}
            {selectedItem.status === 'claim_created' && selectedItem.claim_number && (
              <Card className="p-4 bg-emerald-50 border-emerald-200">
                <div className="flex items-center gap-2">
                  <FileCheck className="w-5 h-5 text-emerald-600" />
                  <div>
                    <p className="font-medium text-emerald-800">Claim Created</p>
                    <p className="text-sm text-emerald-600">
                      Claim #{selectedItem.claim_number}
                      <Button
                        variant="ghost"
                        size="sm"
                        className="ml-2 text-emerald-700"
                        onClick={() => onNavigate?.('billing-claims')}
                      >
                        View in Claims
                      </Button>
                    </p>
                  </div>
                </div>
              </Card>
            )}

            {selectedItem.status === 'rejected' && (
              <Card className="p-4 bg-red-50 border-red-200">
                <div className="flex items-center gap-2">
                  <XCircle className="w-5 h-5 text-red-600" />
                  <div>
                    <p className="font-medium text-red-800">Rejected</p>
                    {selectedItem.status_message && (
                      <p className="text-sm text-red-600">{selectedItem.status_message}</p>
                    )}
                  </div>
                </div>
              </Card>
            )}

            {/* Review History */}
            {selectedItem.review_history && selectedItem.review_history.length > 0 && (
              <Card className="p-4">
                <h3 className="text-sm font-semibold text-gray-700 mb-2">Review History</h3>
                <div className="space-y-2">
                  {selectedItem.review_history.map((action: ReviewAction) => (
                    <div key={action.id} className="flex items-start gap-2 text-xs text-gray-600">
                      <span className="text-gray-400">{new Date(action.performed_at).toLocaleString()}</span>
                      <span className="font-medium">{action.action_type}</span>
                      {action.performer_email && <span>by {action.performer_email}</span>}
                      {action.notes && <span className="text-gray-400">- {action.notes}</span>}
                    </div>
                  ))}
                </div>
              </Card>
            )}
          </div>
        </div>
      </>
    );
  };

  // ---- Main Render ----

  return (
    <div>
      {view === 'list' ? renderList() : renderDetail()}
    </div>
  );
}
